import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, UserPlus, XCircle } from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Header } from "@/components/header";
import { Badge, Button, Card, Field } from "@/components/kit";
import { Sheet } from "@/components/sheet";
import { TemplatesSection } from "@/components/tier-templates";
import { useToast } from "@/components/toast";
import { C, date } from "@/lib/format";

function Deletions({ dels, busy, run }) {
  return (
    <Card className="flex flex-col gap-3">
      <h2 className="t-h3">Demandes de suppression de compte</h2>
      {(dels.data ?? []).length === 0 ? <p className="t-caption">Aucune demande.</p> : dels.data.map((r) => (
        <div key={r.id} data-testid={`deletion-${r.id}`} className="flex flex-col gap-2 border-b border-[#222222] py-2">
          <p className="text-sm text-white">{r.agent_name} · {r.agent_email}</p>
          <p className="t-caption">Le {date(r.created_at)}{r.reason ? ` · « ${r.reason} »` : ""}</p>
          {r.status === "pending" ? (
            <div className="flex gap-2">
              <Button testID={`deletion-approve-${r.id}`} title="Anonymiser" variant="danger" className="flex-1" loading={busy === r.id}
                onClick={() => run(r.id, async () => { await api(`/admin/deletion-requests/${r.id}`, { method: "POST", body: { action: "approve" } }); dels.refetch(); }, "Compte supprimé (données financières conservées).")} />
              <Button testID={`deletion-reject-${r.id}`} title="Refuser" variant="secondary" className="flex-1"
                onClick={() => run(r.id, async () => { await api(`/admin/deletion-requests/${r.id}`, { method: "POST", body: { action: "reject" } }); dels.refetch(); }, "Demande refusée.", "info")} />
            </div>
          ) : <Badge label={r.status === "approved" ? "Traitée" : "Refusée"} color={r.status === "approved" ? C.success : C.silver} />}
        </div>
      ))}
      <p className="t-caption">Les commissions et paiements sont conservés conformément aux obligations légales ; seules les données personnelles sont anonymisées.</p>
    </Card>
  );
}

function Staff({ staff, isSuper, user, run, onAdd }) {
  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h2 className="t-h3">Équipe &amp; droits d'accès</h2>
        {isSuper && <Button testID="staff-add-button" title="Ajouter" icon={UserPlus} variant="secondary" onClick={onAdd} />}
      </div>
      {(staff.data ?? []).map((m) => (
        <div key={m.id} data-testid={`staff-${m.id}`} className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-sm text-white">{m.name}</p>
            <p className="t-caption truncate">{m.email}</p>
          </div>
          <Badge label={m.permission === "super" ? "Administrateur" : "Collaborateur"} color={m.permission === "super" ? C.brand : C.silver} />
          {isSuper && m.id !== user?.id && (
            <button type="button" aria-label={`Retirer l'accès de ${m.name}`} data-testid={`staff-remove-${m.id}`} className="focus-ring flex h-11 w-11 items-center justify-center rounded-full text-[#EB5757] hover:bg-[#EB5757]/10"
              onClick={() => run("rm", async () => { await api(`/admin/staff/${m.id}`, { method: "DELETE" }); staff.refetch(); }, "Accès retiré.")}>
              <XCircle className="h-5 w-5" />
            </button>
          )}
        </div>
      ))}
    </Card>
  );
}

export default function AdminSettings() {
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const { user, logout } = useAuth();
  const isSuper = !!user && user.permission === "super";
  const s = useQuery({ queryKey: ["admin-settings"], queryFn: () => api("/admin/settings") });
  const staff = useQuery({ queryKey: ["admin-staff"], queryFn: () => api("/admin/staff") });
  const dels = useQuery({ queryKey: ["admin-deletions"], queryFn: () => api("/admin/deletion-requests"), enabled: isSuper });
  const dash = useQuery({ queryKey: ["admin-dashboard"], queryFn: () => api("/admin/dashboard") });
  const [prizes, setPrizes] = useState({ first: "", second: "", third: "" });
  const [busy, setBusy] = useState(null);
  const [staffOpen, setStaffOpen] = useState(false);
  const [nf, setNf] = useState({ name: "", email: "", password: "" });
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");

  useEffect(() => {
    if (s.data) setPrizes({ first: s.data.prizes.first, second: s.data.prizes.second, third: s.data.prizes.third });
  }, [s.data]);

  const run = async (key, fn, msg, kind) => {
    setBusy(key);
    try {
      await fn();
      if (msg) toast(msg, kind);
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="admin-settings-screen" className="flex flex-col">
      <Header title="Paramètres" subtitle={user ? `${user.name} · ${isSuper ? "Administrateur" : "Collaborateur"}` : undefined} />
      <div className="mx-auto grid w-full max-w-6xl items-start gap-4 p-4 pb-8 md:p-8 lg:grid-cols-2">
        <div className="flex flex-col gap-4">
          <Card className="flex flex-col gap-3">
            <p className="t-caption">Les barèmes de commission se définissent agent par agent, depuis la fiche de chaque agent (onglet Agents).</p>
            <h2 className="t-h3">Récompenses de fin d'année</h2>
            <Field testID="prize-first-input" label="1er prix" value={prizes.first} onChangeText={(v) => setPrizes((p) => ({ ...p, first: v }))} disabled={!isSuper} />
            <Field testID="prize-second-input" label="2e prix" value={prizes.second} onChangeText={(v) => setPrizes((p) => ({ ...p, second: v }))} disabled={!isSuper} />
            <Field testID="prize-third-input" label="3e prix" value={prizes.third} onChangeText={(v) => setPrizes((p) => ({ ...p, third: v }))} disabled={!isSuper} />
            {isSuper && (
              <Button testID="settings-save-button" title="Enregistrer les paramètres" loading={busy === "settings"}
                onClick={() => run("settings", async () => { qc.setQueryData(["admin-settings"], await api("/admin/settings", { method: "PUT", body: { prizes } })); }, "Paramètres enregistrés.")} />
            )}
          </Card>
          {isSuper && <TemplatesSection missingCount={dash.data?.agents_without_tiers} />}
        </div>
        <div className="flex flex-col gap-4">
          <Staff staff={staff} isSuper={isSuper} user={user} run={run} onAdd={() => setStaffOpen(true)} />
          {isSuper && <Deletions dels={dels} busy={busy} run={run} />}
          <Card className="flex flex-col gap-3">
            <h2 className="t-h3">Mon mot de passe</h2>
            <Field testID="admin-current-password-input" label="Mot de passe actuel" type="password" value={curPw} onChangeText={setCurPw} />
            <Field testID="admin-new-password-input" label="Nouveau mot de passe" type="password" value={newPw} onChangeText={setNewPw} />
            <Button testID="admin-change-password-button" title="Modifier" variant="secondary" loading={busy === "pw"}
              onClick={() => run("pw", async () => { await api("/me/password", { method: "PUT", body: { current_password: curPw, new_password: newPw } }); setCurPw(""); setNewPw(""); }, "Mot de passe modifié.")} />
          </Card>
          <Button testID="admin-logout-button" title="Se déconnecter" icon={LogOut} variant="secondary" onClick={async () => { await logout(); navigate("/welcome", { replace: true }); }} />
        </div>
      </div>
      <Sheet visible={staffOpen} onClose={() => setStaffOpen(false)} title="Nouveau collaborateur" testID="staff-sheet">
        <div className="flex flex-col gap-3">
          <Field testID="staff-name-input" label="Nom" value={nf.name} onChangeText={(v) => setNf((p) => ({ ...p, name: v }))} />
          <Field testID="staff-email-input" label="E-mail (identifiant)" type="email" value={nf.email} onChangeText={(v) => setNf((p) => ({ ...p, email: v }))} />
          <Field testID="staff-password-input" label="Mot de passe (8 caractères min.)" type="password" value={nf.password} onChangeText={(v) => setNf((p) => ({ ...p, password: v }))} />
          <p className="t-caption">Le collaborateur accède aux dossiers et agents, sans accès aux finances, RIB ni paramètres.</p>
          <Button testID="staff-create-button" title="Créer l'accès" loading={busy === "staff"}
            onClick={() => run("staff", async () => {
              await api("/admin/staff", { method: "POST", body: { ...nf, email: nf.email.trim(), permission: "collaborator" } });
              setNf({ name: "", email: "", password: "" }); setStaffOpen(false); staff.refetch();
            }, "Collaborateur ajouté.")} />
        </div>
      </Sheet>
    </div>
  );
}
